import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { CustomerTierRuleToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useCustomerTiersList } from '~/hooks/use-customer-tiers';
import { useCustomerTierCriteriaTypes, useCustomerTierRuleMutations } from '~/hooks/use-customer-tier-rules';
import { customerTierRuleSchema, EMPTY_TIER_RULE, tokenToForm, type CustomerTierRuleFormValues } from '~/lib/schemas/customer-tier-rule';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	row: CustomerTierRuleToken | null;
}

/** Add / edit a customer tier rule (Κανόνας επιπέδου). */
export function TierRuleEditDialog({ open, onOpenChange, row }: Props) {
	const { t } = useTranslation();
	const { create, update } = useCustomerTierRuleMutations();
	const { data: tiers } = useCustomerTiersList();
	const { data: criteriaTypes } = useCustomerTierCriteriaTypes();

	const form = useForm<CustomerTierRuleFormValues>({
		resolver: zodResolver(customerTierRuleSchema) as Resolver<CustomerTierRuleFormValues>,
		defaultValues: row ? tokenToForm(row) : EMPTY_TIER_RULE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, control, formState } = form;
	const isEdit = Boolean(row?.id);

	useEffect(() => {
		if (open) reset(row ? tokenToForm(row) : EMPTY_TIER_RULE);
	}, [open, row, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync({ form: values, original: row });
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const fld = (k: string) => t(`loyalty.tierRules.fields.${k}`);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('loyalty.tierRules.editTitle') : t('loyalty.tierRules.addTitle')}</DialogTitle>
					<DialogDescription>{t('loyalty.tierRules.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<FormField label={fld('tier')} error={formState.errors.c_tier_id?.message}>
							<Controller
								control={control}
								name="c_tier_id"
								render={({ field }) => (
									<Select value={field.value ? String(field.value) : ''} onValueChange={v => field.onChange(Number(v))}>
										<SelectTrigger aria-invalid={Boolean(formState.errors.c_tier_id)}>
											<SelectValue placeholder="—" />
										</SelectTrigger>
										<SelectContent>
											{(tiers ?? []).map(tier => (
												<SelectItem key={tier.id} value={String(tier.id)}>
													{tier.nm}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
						</FormField>
						<FormField label={fld('criteriaType')} error={formState.errors.c_tier_ctype?.message}>
							<Controller
								control={control}
								name="c_tier_ctype"
								render={({ field }) => (
									<Select value={String(field.value ?? 0)} onValueChange={v => field.onChange(Number(v))}>
										<SelectTrigger>
											<SelectValue placeholder="—" />
										</SelectTrigger>
										<SelectContent>
											{(criteriaTypes ?? []).map(ct => (
												<SelectItem key={ct.id} value={String(ct.id)}>
													{ct.nm}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
						</FormField>
						<FormField label={fld('value')} error={formState.errors.c_tier_cvalue?.message}>
							<Input
								type="number"
								step="any"
								{...register('c_tier_cvalue', { valueAsNumber: true })}
								aria-invalid={Boolean(formState.errors.c_tier_cvalue)}
							/>
						</FormField>
						<FormField label={fld('timeFrameMonth')} error={formState.errors.time_frame_month?.message}>
							<Input
								type="number"
								{...register('time_frame_month', { valueAsNumber: true })}
								aria-invalid={Boolean(formState.errors.time_frame_month)}
							/>
						</FormField>
					</div>
					<FormField label={fld('description')} error={formState.errors.descr?.message}>
						<Textarea {...register('descr')} maxLength={200} rows={3} />
					</FormField>
					<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
						<Label htmlFor="tr-act">{fld('active')}</Label>
						<Switch id="tr-act" checked={watch('act')} onCheckedChange={next => setValue('act', next, { shouldDirty: true })} />
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
